import { useMemo, useCallback, useEffect, useRef } from 'react';
import TranscriptPagingUtil from 'utils/TranscriptPagingUtil';
import { buildParagraphs, getParagraphIndexByLocation } from 'utils/ParagraphUtils';
import LocalAPI from 'utils/LocalAPI';

const useTranscriptScroll = ({
  transcriptData,
  transcriptID,
  page,
  shouldGotoOtherPage,
  snippet,
  search,
  pos,
  select,
  selectedExcerptId,
  onDeselect
}) => {
  const scrollbar = useRef(null);
  const paragraphRefs = useRef({});
  const hasScrolled = useRef(false);
  const waitToScroll = search || pos || snippet;

  // Determine which position to use for scroll
  const scrollPosition = useMemo(() => {
    if (snippet) {
      if (!select?.start || !selectedExcerptId || snippet != selectedExcerptId) return null;
      return select.start;
    } else if (!search) {
      return pos;
    }
    return null;
  }, [snippet, select?.start, selectedExcerptId, search, pos]);

  // Calculate paragraph index from position
  const paragraphIndex = useMemo(() => {
    if (!scrollPosition || !transcriptData?.body) return null;

    const paragraphs = buildParagraphs(transcriptData.body);
    const numberOfPages = TranscriptPagingUtil.getNumberOfPages(transcriptData.body.length);
    
    const absoluteParagraphIndex = getParagraphIndexByLocation(paragraphs, scrollPosition);
    
    const paragraphPage = TranscriptPagingUtil.getPageLocationOfParagraph(
      absoluteParagraphIndex, 
      numberOfPages, 
      paragraphs.length
    );
    
    if (paragraphPage != (page || 1) && !search) { 
      shouldGotoOtherPage(paragraphPage);
      return null;
    }

    const paragraphStart = TranscriptPagingUtil.getPageStart(paragraphPage, numberOfPages, paragraphs.length);
    const relativeParagraphIndex = absoluteParagraphIndex - paragraphStart;
    
    return relativeParagraphIndex;
  }, [scrollPosition, page, search, shouldGotoOtherPage, transcriptData?.body]);

  // Reset scroll flag when selection actually changes (not when calculations change)
  useEffect(() => {
    hasScrolled.current = false;
  }, [transcriptID, page, snippet, pos]);

  // Track whether data is loaded (not the data itself)
  const isDataLoaded = !!transcriptData?.body;

  // Perform the actual scroll
  useEffect(() => {
    if (!isDataLoaded || hasScrolled.current || !scrollbar.current) {
      return;
    }

    if (!!paragraphIndex) {
      const paragraphDiv = paragraphRefs.current[paragraphIndex];
      if (!paragraphDiv) return;

      const y = paragraphDiv.offsetTop;
      if (y) {
        scrollbar.current.scrollTop(y);
        hasScrolled.current = true;
      }
    } else if (!waitToScroll) {
      const { loc, page: saved_page } = LocalAPI.getTranscriptLocation(transcriptID) || {};

      if (!loc) return;

      if ((saved_page || '1') != (page || '1')) {
        shouldGotoOtherPage(saved_page);
        return;
      }

      scrollbar.current.scrollTop(loc);
      hasScrolled.current = true;
    }
  }, [isDataLoaded, paragraphIndex, transcriptID, page, waitToScroll, shouldGotoOtherPage]);

  // Handle scroll stop to save position
  const handleScrollStop = useCallback(() => {
    if (transcriptID && scrollbar.current) {
      const scrollTop = scrollbar.current.getScrollTop();
      if (!scrollTop) return;

      LocalAPI.setTranscriptLocation(transcriptID, scrollTop, page || 1);
    }
  }, [transcriptID, page]);

  // Set paragraph ref callback
  const setParagraphRef = useCallback((index, pc) => {
    paragraphRefs.current[index] = pc;
  }, []);

  // Handle page navigation
  const onPage = useCallback((page) => {
    if (scrollbar.current) {
      scrollbar.current.scrollTop(0);
      LocalAPI.setTranscriptLocation(transcriptID, 1, page);
      onDeselect();
    }
  }, [transcriptID, onDeselect]);

  return {
    scrollbar,
    setParagraphRef,
    onPage,
    handleScrollStop
  };
};

export default useTranscriptScroll;